import type { Player, Position, Substitution } from '../types';

export const applyManualSubstitutionOverrides = ({
  fieldPlayers,
  substitutions,
  quarter,
  scheduleBoundaryTime,
  currentTime,
}: {
  fieldPlayers: Record<string, Player | null>;
  substitutions: Substitution[];
  quarter: number;
  scheduleBoundaryTime: number;
  currentTime: number;
}): Record<string, Player | null> => {
  const result = { ...fieldPlayers };
  [...substitutions]
    .filter(substitution => (
      substitution.quarter === quarter
      && substitution.time >= scheduleBoundaryTime
      && substitution.time <= currentTime
      && substitution.reason !== 'scheduled'
    ))
    .sort((left, right) => left.time - right.time)
    .forEach(substitution => {
      const occupiedPosition = result[substitution.position]?.id === substitution.playerOut.id
        ? substitution.position
        : Object.entries(result).find(([, player]) => player?.id === substitution.playerOut.id)?.[0];
      if (occupiedPosition) result[occupiedPosition] = substitution.playerIn;
    });
  return result;
};

export const applyLiveSubstitution = ({
  lineup,
  reserves,
  position,
  playerOut,
  playerIn,
}: {
  lineup: Record<Position, Player | null> | null;
  reserves: Player[];
  position: Position;
  playerOut: Player;
  playerIn: Player;
}): { lineup: Record<Position, Player | null>; reserves: Player[]; position: Position } => {
  const currentLineup = lineup ?? {};
  const resolvedPosition = currentLineup[position]?.id === playerOut.id
    ? position
    : Object.entries(currentLineup).find(([, player]) => player?.id === playerOut.id)?.[0];

  if (!resolvedPosition) {
    throw new Error('Outgoing player is no longer on the field');
  }
  if (!reserves.some(player => player.id === playerIn.id)) {
    throw new Error('Incoming player is no longer on the bench');
  }

  return {
    position: resolvedPosition,
    lineup: { ...currentLineup, [resolvedPosition]: playerIn },
    reserves: [
      ...reserves.filter(player => player.id !== playerIn.id && player.id !== playerOut.id),
      playerOut,
    ],
  };
};
